import { useState } from "react";

import {
  PoliciesService,
  type CreateVersionRequest,
  type PolicyRuleDefinition,
  type PolicyRuleResponse,
  type PolicyVersionResponse,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import {
  parseApiError,
  parseValidationDetail,
} from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { JsonEditor } from "@/components/shared/json-editor";

interface PolicyVersionDialogProps {
  rule: PolicyRuleResponse | null;
  /** Pre-fill the editor with the current active rule_definition.
   *  Pass ``null`` to seed with the empty DSL skeleton. */
  initialRule: PolicyRuleDefinition | null;
  onClose: () => void;
  onSuccess: () => void;
}

/** Empty DSL skeleton — mirrors the backend ``PolicyRuleDefinition``
 *  Pydantic shape so a freshly-opened editor lands on a working
 *  starter rather than an empty object. */
const EMPTY_DSL: PolicyRuleDefinition = {
  name: "Nama kebijakan",
  description: "",
  when: {
    operator: "and",
    conditions: [
      {
        field: "user.team",
        operator: "equals",
        value: "<team>",
      },
    ],
  },
  effect: {
    type: "deny",
    deny_reason_code: "<CODE>",
    deny_user_message: "Pesan singkat untuk user (Bahasa Indonesia).",
  },
  priority: 50,
};

/**
 * Create a new version of an existing policy rule and immediately
 * activate it. The previous active version is archived
 * server-side.
 */
export function PolicyVersionDialog({
  rule,
  initialRule,
  onClose,
  onSuccess,
}: PolicyVersionDialogProps) {
  const { t } = useTranslation();
  const [ruleJson, setRuleJson] = useState(() =>
    JSON.stringify(initialRule ?? EMPTY_DSL, null, 2)
  );
  const [changelog, setChangelog] = useState("");
  const [validRule, setValidRule] = useState<PolicyRuleDefinition | null>(
    initialRule
  );
  const [validationDetails, setValidationDetails] = useState<string[]>([]);

  const createVersion = useMutation<
    CreateVersionRequest,
    PolicyVersionResponse
  >((body) => PoliciesService.createVersion(rule!.id, body), {
    onError: (err) => setValidationDetails(parseValidationDetail(err)),
  });

  const activate = useMutation<string, PolicyVersionResponse>(
    (versionId) => PoliciesService.activateVersion(rule!.id, versionId),
    {
      onSuccess: () => {
        setChangelog("");
        setValidationDetails([]);
        onSuccess();
      },
    }
  );

  if (!rule) return null;

  const isLoading = createVersion.isLoading || activate.isLoading;
  const error = createVersion.error ?? activate.error;
  const canSubmit = validRule !== null;

  const handleClose = () => {
    createVersion.reset();
    activate.reset();
    setValidationDetails([]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !validRule) return;
    setValidationDetails([]);
    const body: CreateVersionRequest = { rule_definition: validRule };
    const trimmedChangelog = changelog.trim();
    if (trimmedChangelog) body.changelog = trimmedChangelog;
    const version = await createVersion.mutate(body);
    if (version) {
      await activate.mutate(version.id);
    }
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-2xl">
      <DialogHeader>
        <DialogTitle>
          {t("policies.versionDialog.title", { name: rule.name })}
        </DialogTitle>
        <DialogDescription>
          {t("policies.versionDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-ocean-900">
                {t("policies.versionDialog.ruleLabel")}
              </span>
              <span className="text-xs text-neutral-500">
                {t("policies.versionDialog.ruleHint")}
              </span>
            </div>
            <JsonEditor
              value={ruleJson}
              onChange={setRuleJson}
              onValidChange={(parsed) =>
                setValidRule(
                  parsed && typeof parsed === "object"
                    ? (parsed as PolicyRuleDefinition)
                    : null
                )
              }
              rows={12}
            />
          </div>

          <FormField
            label={t("policies.versionDialog.changelogLabel")}
            value={changelog}
            onChange={setChangelog}
            placeholder={t("policies.versionDialog.changelogPlaceholder")}
            maxLength={500}
            hint={t("policies.versionDialog.changelogHint")}
          />

          {error && (
            <Alert variant="danger">
              <div className="space-y-1.5">
                <div className="text-sm font-medium">
                  {parseApiError(error)}
                </div>
                {validationDetails.length > 0 && (
                  <ul className="ml-4 list-disc text-xs">
                    {validationDetails.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Alert>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("policies.versionDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading || !canSubmit}>
            {isLoading
              ? t("policies.versionDialog.submitLoading")
              : t("policies.versionDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
